import {
  AttachMoney,
  CheckCircle,
  Inventory,
  Pending,
  ShoppingCart,
  TrendingUp,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import type {
  AnalyticsData,
  OrdersByCampus,
  OrdersByStatus,
  RevenueByPackage,
} from "../../services/analyticsService";

interface AnalyticsTabProps {
  analytics: AnalyticsData | null;
  loading: boolean;
  error: string;
}

const AnalyticsTab: React.FC<AnalyticsTabProps> = ({
  analytics,
  loading,
  error,
}) => {
  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      </Box>
    );
  }

  if (!analytics) {
    return (
      <Box sx={{ textAlign: "center", py: 8 }}>
        <Typography variant="h6" color="text.secondary">
          No analytics data available
        </Typography>
      </Box>
    );
  }

  const { summary, revenueByPackage, ordersByStatus, ordersByCampus } =
    analytics;

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  // Metric cards data
  const metricCards = [
    {
      title: "Total Packages Ordered",
      value: summary.totalPackagesOrdered.toLocaleString(),
      icon: <ShoppingCart sx={{ fontSize: 40 }} />,
      color: "#892c8b",
      bgColor: "rgba(137, 44, 139, 0.1)",
    },
    {
      title: "Total Income",
      value: formatCurrency(summary.totalIncome),
      icon: <AttachMoney sx={{ fontSize: 40 }} />,
      color: "#10b981",
      bgColor: "rgba(16, 185, 129, 0.1)",
    },
    {
      title: "Paid Orders",
      value: summary.totalPaidOrders.toLocaleString(),
      icon: <CheckCircle sx={{ fontSize: 40 }} />,
      color: "#3b82f6",
      bgColor: "rgba(59, 130, 246, 0.1)",
    },
    {
      title: "Average Order Value",
      value: formatCurrency(summary.averageOrderValue),
      icon: <TrendingUp sx={{ fontSize: 40 }} />,
      color: "#f59e0b",
      bgColor: "rgba(245, 158, 11, 0.1)",
    },
    {
      title: "Pending Orders",
      value: summary.pendingOrders.toLocaleString(),
      icon: <Pending sx={{ fontSize: 40 }} />,
      color: "#ef4444",
      bgColor: "rgba(239, 68, 68, 0.1)",
    },
    {
      title: "Completed Orders",
      value: summary.completedOrders.toLocaleString(),
      icon: <Inventory sx={{ fontSize: 40 }} />,
      color: "#8b5cf6",
      bgColor: "rgba(139, 92, 246, 0.1)",
    },
  ];

  return (
    <Box>
      {/* Summary Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {metricCards.map((card, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
            <Card
              sx={{
                height: "100%",
                transition: "all 0.2s ease-in-out",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {card.title}
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{
                        fontWeight: 700,
                        color: card.color,
                      }}
                    >
                      {card.value}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: 2,
                      bgcolor: card.bgColor,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: card.color,
                    }}
                  >
                    {card.icon}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Revenue by Package */}
      {revenueByPackage.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: "text.primary",
            }}
          >
            Revenue by Package
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Package Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Orders
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Revenue
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {revenueByPackage.map((item: RevenueByPackage) => (
                  <TableRow key={item.packageId}>
                    <TableCell>{item.packageName}</TableCell>
                    <TableCell align="right">{item.orderCount}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatCurrency(item.revenue)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Orders by Status */}
      {ordersByStatus.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: "text.primary",
            }}
          >
            Orders by Status
          </Typography>
          <Grid container spacing={2}>
            {ordersByStatus.map((item: OrdersByStatus) => {
              const totalOrders = summary.totalPackagesOrdered;
              const percentage =
                totalOrders > 0 ? (item.count / totalOrders) * 100 : 0;

              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={item.status}>
                  <Card>
                    <CardContent>
                      <Box sx={{ mb: 1 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5 }}
                        >
                          {item.status.replace(/_/g, " ").toUpperCase()}
                        </Typography>
                        <Typography variant="h5" sx={{ fontWeight: 600 }}>
                          {item.count}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={percentage}
                        sx={{
                          height: 8,
                          borderRadius: 1,
                          bgcolor: "rgba(0, 0, 0, 0.1)",
                        }}
                      />
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {percentage.toFixed(1)}% of total orders
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {/* Orders by Campus */}
      {ordersByCampus.length > 0 && (
        <Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 600,
              mb: 2,
              color: "text.primary",
            }}
          >
            Orders by Campus
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Campus Name</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    Order Count
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordersByCampus
                  .sort((a, b) => b.count - a.count)
                  .map((item: OrdersByCampus) => (
                    <TableRow key={item.campusId}>
                      <TableCell>{item.campusName}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {item.count}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default AnalyticsTab;
